import { createReducer, on } from '@ngrx/store';
import * as LocationCardActions from '../actions';
import { findFieldById, findSectionById } from '../configs/base-utils';
import { TritonInfoFieldId, TritonSectionIds, Mp1SensorIds, Mp2SensorIds, Mp1SensorTitles, Mp2SensorTitles, TritonSensors, getSensorFieldId, getSensorKeyById } from '../configs/triton/base-config-models';
import { normalizeSection, isFormField } from '../config.models';
import { samplerConfig } from '../configs/triton/sensors/sampler';
import { FieldType, SelectField } from '../models/field-models';
import { checkTritonSameSensorError, setTritonSensorPreferences, sortTritonSections, tritonCheckMp2, tritonMonitorPointChange, tritonSensorChangeUpdates } from '../configs/triton/utils';
import { getTritonSensorConfigByName } from '../configs/triton/base';
import { CommportValue, GAIN_CONTROL_AUTO_NUMERIC, TritonSensorNames } from '../../location-card.constants';
import { initialState } from './state'
import { SensorTypes } from '../configs/common.models';
import { TritonLocationCardState } from '../configs/triton/models';
import { required, pattern } from '../core/validator-builders';
import { ValidationRuleType } from '../models/validator-models';
import { LongRangeSensorFieldIds, SamplerFieldIds } from '../configs/triton/sensors/config-models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';

const tritonInitialState: TritonLocationCardState = {
    ...(initialState as TritonLocationCardState),
    mp2LastBasicInfo: null,
};

export const tritonReducer = createReducer(
    tritonInitialState,
    on(LocationCardActions.commportChange, (state, { commport }) => {
        const formState = { ...state.formState };

        const tiltAlarmThresholdField = findFieldById(formState, TritonInfoFieldId.TiltAlarmThreshold, FieldType.Number);
        const samplerSection = formState.config.sections.find((section) => section.id === TritonSectionIds.Sampler);

        const sensor1Field = findFieldById(formState, TritonInfoFieldId.Sensor1, FieldType.Select);
        const mp1Field = findFieldById(formState, TritonInfoFieldId.MP1, FieldType.Select);
        const channel1Field = findFieldById(formState, TritonInfoFieldId.Channel1, FieldType.Select);

        if (commport === CommportValue.None) {
            tiltAlarmThresholdField.visible = false;
            tiltAlarmThresholdField.validators = (tiltAlarmThresholdField.validators || []).filter(
                (v) => v.type !== ValidationRuleType.Required,
            );

            if (samplerSection) {
                formState.config.sections = formState.config.sections.filter((v) => v.id !== TritonSectionIds.Sampler);
            }

            if (sensor1Field) sensor1Field.validators = [required()];
            if (mp1Field) mp1Field.validators = [required()];
            if (channel1Field) channel1Field.validators = [required()];
        } else if (commport === CommportValue.Inclinometer) {
            tiltAlarmThresholdField.visible = true;
            const hasRequired = tiltAlarmThresholdField.validators?.some((v) => v.type === ValidationRuleType.Required);
            if (!hasRequired) {
                tiltAlarmThresholdField.validators = [required(), ...(tiltAlarmThresholdField.validators || [])];
            }
            if (tiltAlarmThresholdField.value == null) {
                tiltAlarmThresholdField.value = 5;
            }

            if (samplerSection) {
                formState.config.sections = formState.config.sections.filter((v) => v.id !== TritonSectionIds.Sampler);
            }

            if (sensor1Field) sensor1Field.validators = [];
            if (mp1Field) mp1Field.validators = [];
            if (channel1Field) channel1Field.validators = [];
        } else if (commport === CommportValue.Sampler) {
            tiltAlarmThresholdField.visible = false;
            tiltAlarmThresholdField.validators = (tiltAlarmThresholdField.validators || []).filter(
                (v) => v.type !== ValidationRuleType.Required,
            );

            if (!samplerSection) {
                const sampler = normalizeSection(samplerConfig(state.settings));
                formState.config.sections.push(sampler);
            }

            if (sensor1Field) sensor1Field.validators = [];
            if (mp1Field) mp1Field.validators = [];
            if (channel1Field) channel1Field.validators = [];
        }

        return { ...state, formState };
    }),

    on(LocationCardActions.tritonSensorChange, (state, { sensor, index }) => {
        const formState = { ...state.formState };
        const tritonSection = formState.config.sections.find((section) => section.id === TritonSectionIds.TritonInfo);

        if (!tritonSection) {
            console.error(`Triton section not found`);
            return state;
        }

        const mp1Field = tritonSection.fields[TritonInfoFieldId.MP1] as SelectField<unknown, number>;
        const mp2Field = tritonSection.fields[TritonInfoFieldId.MP2] as SelectField<unknown, number>;
        const channel1Field = tritonSection.fields[TritonInfoFieldId.Channel1];
        const channel2Field = tritonSection.fields[TritonInfoFieldId.Channel2];
        const mpValue = (index === 0 ? mp1Field.value ?? 0 : mp2Field.value ?? 1) as 0 | 1;

        formState.config.sections = formState.config.sections.filter(
            (v) => !v.metadata || v.metadata.type !== SensorTypes.triton || v.metadata.sensorIndex !== index,
        );

        // None sensor selected
        if (sensor.value === '') {
            if (index === 0) {
                mp1Field.value = null;
                channel1Field.value = null;
            } else {
                mp2Field.value = null;
                mp2Field.disabled = true;
                mp2Field.validators = (mp2Field.validators || []).filter((v) => v.type !== ValidationRuleType.Required);

                channel2Field.value = null;
                channel2Field.disabled = true;
                if (isFormField(channel2Field)) {
                    channel2Field.validators = (channel2Field.validators || []).filter((v) => v.type !== ValidationRuleType.Required);
                }

                const silt2Field = tritonSection.fields[TritonInfoFieldId.Silt2];
                if (silt2Field) {
                    silt2Field.value = null;
                }
            }

            tritonCheckMp2(state);
            sortTritonSections(formState);

            const newState = { ...state, formState };
            setTritonSensorPreferences(newState);
            checkTritonSameSensorError(newState.formState);
            return { ...newState, options: { ...state.options } };
        }

        const newSensor = getTritonSensorConfigByName(
            sensor.text as TritonSensorNames,
            index as 0 | 1,
            index as 0 | 1,
            state.settings,
        );

        const normalizedSensor = normalizeSection(newSensor);
        normalizedSensor.metadata = { sensorIndex: index, mp: mpValue, type: SensorTypes.triton };
        const sensorKey = getSensorKeyById(normalizedSensor.id);
        normalizedSensor.title = mpValue === 0 ? Mp1SensorTitles[sensorKey] : Mp2SensorTitles[sensorKey];
        formState.config.sections.push(normalizedSensor);

        // first change handler
        const section1FirstChange = mp1Field.value === null && mp2Field.value === null;
        if (section1FirstChange) {
            mp1Field.value = 0;
            channel1Field.value = 1;
        }

        const section2FirstChange = index === 1 && mp2Field.value === null;
        if (section2FirstChange) {
            mp2Field.disabled = false;
            channel2Field.disabled = false;
            mp2Field.value = 1;
            channel2Field.value = channel1Field.value === 1 ? 2 : 1;
        }

        if (index === 1) {
            const hasRequired = (validators: typeof mp2Field.validators) =>
                validators?.some((v) => v.type === ValidationRuleType.Required);
            if (!hasRequired(mp2Field.validators)) {
                mp2Field.validators = [...(mp2Field.validators || []), required()];
            }
            if (isFormField(channel2Field) && !hasRequired(channel2Field.validators)) {
                channel2Field.validators = [...(channel2Field.validators || []), required()];
            }
        }

        const siltFieldId = index === 0 ? TritonInfoFieldId.Silt1 : TritonInfoFieldId.Silt2;
        const siltField = tritonSection.fields[siltFieldId];
        if (siltField && !siltField.value) {
            siltField.value = 0;
        }

        tritonCheckMp2(state);
        sortTritonSections(formState);
        setTritonSensorPreferences(state);
        checkTritonSameSensorError(formState);
        tritonSensorChangeUpdates(formState);

        return { ...state, formState, options: { ...state.options } };
    }),

    on(LocationCardActions.tritonChannelChange, (state, { index }) => {
        const formState = { ...state.formState };
        const tritonInfo = formState.config.sections.find((section) => section.id === TritonSectionIds.TritonInfo);
        const channel1 = tritonInfo.fields[TritonInfoFieldId.Channel1];
        const channel2 = tritonInfo.fields[TritonInfoFieldId.Channel2];

        const updatedChannel = index === 0 ? channel1 : channel2;
        const toUpdateChannel = index === 0 ? channel2 : channel1;

        if (updatedChannel.value === toUpdateChannel.value) {
            toUpdateChannel.value = updatedChannel.value === 1 ? 2 : 1;
        }

        setTritonSensorPreferences(state);
        return { ...state, formState, options: { ...state.options } };
    }),

    on(LocationCardActions.tritonMonitorPointChange, (state, { index }) => {
        const formState = { ...state.formState };
        tritonMonitorPointChange(formState, index);
        tritonCheckMp2(state);
        sortTritonSections(formState);
        setTritonSensorPreferences(state);
        checkTritonSameSensorError(formState);

        return { ...state, formState, options: { ...state.options } };
    }),

    on(LocationCardActions.setTritonSiltValue, (state, { mp, value }) => {
        const formState = { ...state.formState };
        const tritonInfo = formState.config.sections.find((section) => section.id === TritonSectionIds.TritonInfo);

        if (!tritonInfo) {
            console.error(`Triton section not found`);
            return state;
        }

        const siltField1 = findFieldById(formState, TritonInfoFieldId.Silt1, FieldType.Number);
        const siltField2 = findFieldById(formState, TritonInfoFieldId.Silt2, FieldType.Number);

        if (mp === 0 && siltField1) {
            siltField1.value = value ?? 0;
        } else if (mp === 1 && siltField2) {
            siltField2.value = value ?? 0;
        }

        return { ...state, formState };
    }),

    on(LocationCardActions.samplerTypeChange, (state, { samplingType }) => {
        const formState = { ...state.formState };
        const triggerField = findFieldById(formState, SamplerFieldIds.Trigger, FieldType.Number);

        if (!triggerField) {
            return state;
        }

        if (!samplingType) {
            triggerField.validators = [];
            triggerField.placeholder = 'COMMON.TRIGGER';
            return { ...state, formState };
        }

        const isMetric = state.settings.isMetric;
        const flowUnit = state.settings.flowUnit;
        let validatorPattern: RegExp | string;

        if (samplingType === 1) {
            validatorPattern = isMetric
                ? REGEX_CONFIG.numericDecimal1PlacePattern
                : REGEX_CONFIG.numericDecimal2PlacePattern;
        } else if (samplingType === 2) {
            validatorPattern = isMetric
                ? REGEX_CONFIG.numericDecimal1PlacePattern
                : REGEX_CONFIG.numericDecimal3PlacePattern;
        } else if (samplingType === 3) {
            validatorPattern = flowUnit === 'cfs'
                ? REGEX_CONFIG.numericOnlyPattern
                : REGEX_CONFIG.numericDecimal3PlacePattern;
        } else {
            validatorPattern = REGEX_CONFIG.numericDecimal2PlacePattern;
        }

        triggerField.validators = [
            required(),
            pattern({ value: validatorPattern }),
        ];

        return { ...state, formState };
    }),

    on(LocationCardActions.tritonGainControlChange, (state, { isManual, sensorIndex }) => {
        const formState = { ...state.formState };
        const sectionId = sensorIndex === 0
            ? Mp1SensorIds[TritonSensors.LongRange]
            : Mp2SensorIds[TritonSensors.LongRange];
        const section = findSectionById(formState, sectionId);

        if (!section) {
            return state;
        }

        const gainControlField = section.fields[getSensorFieldId(LongRangeSensorFieldIds.GainControl, sensorIndex as 0 | 1)];

        if (!gainControlField || gainControlField.type !== FieldType.Select) {
            return state;
        }

        if (isManual) {
            gainControlField.visible = true;
            gainControlField.disabled = false;
        } else {
            gainControlField.value = GAIN_CONTROL_AUTO_NUMERIC;
            gainControlField.visible = false;
            gainControlField.disabled = true;
        }

        return { ...state, formState };
    }),
);
